import { useRouter } from "next/router";
import { useMemo } from "react";
import toast from "react-hot-toast";
import useSortClientVisits from "@/components/serviceFlow/client/useSortClientVisits";
import { useClientAccessToken } from "@/contexts/clientAccessTokenContext";
import { useConfirmAppointmentMutation } from "@/graphql/mutations/confirmAppointment.graphql.types";
import {
  ClientByTokenQuery,
  useClientByTokenQuery,
} from "@/graphql/queries/clientByToken.graphql.types";
import useClientFormsByToken from "@/hooks/client/useClientFormsByToken";
import useAction from "@/hooks/misc/useAction";
import { VisitStatus } from "@/types";
import { isFormArchived, shouldShowFormExpiredToClient } from "@/utils/forms";
import useErrorLogger from "@/utils/useErrorLogger";

type Visit =
  ClientByTokenQuery["clientAccessTokenByPk"]["client"]["visits"][number];

export default function useOutstandingItems(
  visits: Visit[],
  forms: ReturnType<typeof useClientFormsByToken>["forms"]
) {
  const logError = useErrorLogger();
  const {
    query: { fromSMS },
  } = useRouter();
  const { clientAccessToken: token, getTokenContext } = useClientAccessToken();
  const { futureVisits } = useSortClientVisits(visits);

  const { refetch: refetchClientForms } = useClientByTokenQuery({
    variables: {
      token: token,
    },
    ...getTokenContext(),
    skip: !token,
  });

  const [confirmAppointmentMutation] = useConfirmAppointmentMutation();

  const confirmAppointment = async (appointmentAccessToken: string) => {
    const mutateAndRefetch = async () => {
      await confirmAppointmentMutation({
        variables: {
          token: appointmentAccessToken,
          fromSms: fromSMS === "true",
        },
        ...getTokenContext(),
        update(cache) {
          cache.modify({
            fields: {
              appointmentPublicInfo(existingData) {
                return {
                  ...existingData,
                  status: VisitStatus.CONFIRMED,
                };
              },
            },
          });
        },
      });
      refetchClientForms();
    };

    try {
      await toast.promise(mutateAndRefetch(), {
        loading: "Confirming appointment...",
        success: "Appointment has been confirmed.",
        error: "Confirmation has failed.",
      });
    } catch (errors) {
      logError(errors);
    }
  };

  const toConfirm = futureVisits.filter(
    (visit) => visit.status === VisitStatus.SCHEDULED
  );

  const servicesInFutureVisits = useMemo(
    () =>
      new Set(
        futureVisits.flatMap((visit) =>
          visit.serviceMenuItemLines.map((line) => line.serviceMenuItem.id)
        )
      ),
    [futureVisits]
  );

  const offeringsInFutureVisits = useMemo(
    () =>
      new Set(
        futureVisits.flatMap((visit) =>
          visit.serviceMenuItemLines.flatMap((line) =>
            line.serviceMenuItem.medspaServiceOfferings.map(
              ({ medspaserviceofferingId }) => medspaserviceofferingId
            )
          )
        )
      ),
    [futureVisits]
  );

  const unsignedForms = useMemo(() => {
    if (!forms || !forms.intakeForms || !forms.consentForms) return [];

    const intakeFormsToShow = forms.intakeForms
      .filter((intakeForm) => {
        if (intakeForm.form.allServices) return true;

        return intakeForm.form.medspaServiceOfferings
          .map(({ medspaserviceofferingId }) => medspaserviceofferingId)
          .some((offeringId) => offeringsInFutureVisits.has(offeringId));
      })
      .filter(
        (intakeForm) =>
          !intakeForm.submittedAt ||
          (!isFormArchived(intakeForm) &&
            shouldShowFormExpiredToClient(intakeForm))
      )
      .map((form) => ({
        name: form.form.title,
        id: form.id,
        url:
          `intake-form/${form.id}` +
          (form.versions.length ? `?version=${form.versions.length}` : ""),
      }));

    const consentFormsToShow = forms.consentForms
      .filter((consentForm) => {
        return consentForm.consentForm.serviceMenuItems
          .map((service) => service.serviceMenuItem.id)
          .some((serviceId) => servicesInFutureVisits.has(serviceId));
      })
      .filter(({ signedAt }) => !signedAt)
      .map((form) => ({
        name: form.consentForm.name,
        id: form.id,
        url: `consent-form/${form.id}`,
      }));

    return [...intakeFormsToShow, ...consentFormsToShow];
  }, [forms, servicesInFutureVisits, offeringsInFutureVisits]);

  const {
    handleAction: confirmAppointmentAction,
    isLoading: isConfirmingAppointment,
  } = useAction(confirmAppointment);

  return {
    unsignedForms,
    toConfirm,
    confirmAppointmentAction,
    isConfirmingAppointment,
    token,
  };
}
